import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Pencil } from 'lucide-react';
import { format } from 'date-fns';

export default function CleaningJobs() {
  const [jobs, setJobs] = useState([]);
  const [units, setUnits] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const defaultForm = { unit_id: '', assigned_to: '', scheduled_date: '', status: 'pending', notes: '', checklist: [{ item: 'Clean bathroom', completed: false }, { item: 'Change linens', completed: false }, { item: 'Vacuum floors', completed: false }, { item: 'Restock supplies', completed: false }] };
  const [form, setForm] = useState(defaultForm);

  const fetchData = async () => {
    try {
      const [j, u, usr] = await Promise.all([api.get('/cleaning-jobs'), api.get('/units'), api.get('/users', { params: { role: 'cleaner' } })]);
      setJobs(j.data); setUnits(u.data); setUsers(usr.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/cleaning-jobs/${editing.id}`, form);
      else await api.post('/cleaning-jobs', form);
      setDialogOpen(false); setEditing(null); setForm(defaultForm); fetchData();
    } catch (err) { console.error(err); }
  };

  const handleEdit = (j) => {
    setEditing(j); setForm({ unit_id: j.unit_id, assigned_to: j.assigned_to || '', scheduled_date: j.scheduled_date ? format(new Date(j.scheduled_date), 'yyyy-MM-dd') : '', status: j.status, notes: j.notes || '', checklist: j.checklist || defaultForm.checklist }); setDialogOpen(true);
  };

  const handleStatusChange = async (id, status) => { await api.put(`/cleaning-jobs/${id}`, { status }); fetchData(); };

  const statusColors = { pending: 'bg-amber-50 text-amber-700 border-amber-200', in_progress: 'bg-blue-50 text-blue-700 border-blue-200', completed: 'bg-emerald-50 text-emerald-700 border-emerald-200' };

  const filtered = filterStatus === 'all' ? jobs : jobs.filter(j => j.status === filterStatus);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" /></div>;

  return (
    <div data-testid="cleaning-page">
      <div className="page-header">
        <h1 className="page-title">Cleaning Jobs</h1>
        <div className="flex gap-3">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36" data-testid="filter-clean-status"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditing(null); setForm(defaultForm); } }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-cleaning-btn"><Plus className="w-4 h-4 mr-2" />Add Job</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit Job' : 'New Cleaning Job'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                <div>
                  <Label>Unit</Label>
                  <Select value={form.unit_id} onValueChange={v => setForm({ ...form, unit_id: v })}>
                    <SelectTrigger data-testid="clean-unit-select"><SelectValue placeholder="Select unit" /></SelectTrigger>
                    <SelectContent>{units.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Assigned To</Label>
                  <Select value={form.assigned_to} onValueChange={v => setForm({ ...form, assigned_to: v })}>
                    <SelectTrigger data-testid="clean-assignee-select"><SelectValue placeholder="Select cleaner" /></SelectTrigger>
                    <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Scheduled Date</Label><Input type="date" value={form.scheduled_date} onChange={e => setForm({ ...form, scheduled_date: e.target.value })} required data-testid="clean-date-input" /></div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-2 block">Checklist</Label>
                  {form.checklist.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 mb-2">
                      <Checkbox checked={item.completed} onCheckedChange={c => { const cl = [...form.checklist]; cl[i] = { ...cl[i], completed: c }; setForm({ ...form, checklist: cl }); }} data-testid={`clean-checklist-${i}`} />
                      <span className="text-sm">{item.item}</span>
                    </div>
                  ))}
                </div>
                <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="clean-notes-input" /></div>
                <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="clean-submit-btn">{editing ? 'Update' : 'Create'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="table-wrap">
        <table className="w-full data-table min-w-[500px]">
          <thead><tr><th>Unit</th><th>Assigned</th><th>Date</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {filtered.map(j => (
              <tr key={j.id} data-testid={`clean-row-${j.id}`}>
                <td className="font-medium text-slate-900">{j.unit_name}</td>
                <td>{j.assigned_name || 'Unassigned'}</td>
                <td>{j.scheduled_date ? format(new Date(j.scheduled_date), 'MMM dd, yyyy') : 'N/A'}</td>
                <td>
                  <Select value={j.status} onValueChange={v => handleStatusChange(j.id, v)}>
                    <SelectTrigger className="h-7 text-xs w-28"><Badge className={statusColors[j.status] || ''}>{j.status?.replace('_', ' ')}</Badge></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td><Button variant="ghost" size="sm" onClick={() => handleEdit(j)} data-testid={`edit-clean-${j.id}`}><Pencil className="w-3.5 h-3.5" /></Button></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-400">No cleaning jobs</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
